import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { lambdaDataService } from '@/services/LambdaDataService';
import { StatusConfig } from '@/hooks/useStatusManagement';

interface StatusConfigContextType {
  quoteStatuses: StatusConfig[];
  orderStatuses: StatusConfig[];
  packageStatuses: StatusConfig[];
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  lastUpdated: number; // Timestamp to trigger re-renders
}

const StatusConfigContext = createContext<StatusConfigContextType | undefined>(undefined);

const defaultQuoteStatuses: StatusConfig[] = [
  {
    id: 'pending',
    name: 'pending',
    label: 'Pending',
    description: 'Quote request is awaiting review',
    color: 'secondary',
    icon: 'Clock',
    isActive: true,
    order: 1,
    allowedTransitions: ['sent', 'rejected'],
    isTerminal: false,
    category: 'quote',
    // Flow properties
    triggersEmail: false,
    requiresAction: true,
    showsInQuotesList: true,
    showsInOrdersList: false,
    canBePaid: false,
    isDefaultQuoteStatus: true,
    // Action permissions
    allowEdit: true,
    allowApproval: false,
    allowRejection: true,
    allowCartActions: false,
    allowCancellation: true,
    allowRenewal: false,
    allowShipping: false,
    allowAddressEdit: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: false,
    isSuccessful: false,
    countsAsOrder: false,
    progressPercentage: 10,
    // Customer messaging
    customerMessage: 'Your quote is being reviewed by our team',
    customerActionText: 'Waiting for Review',
    // CSS styling
    cssClass: 'status-pending',
    badgeVariant: 'secondary',
  },
  {
    id: 'sent',
    name: 'sent',
    label: 'Sent',
    description: 'Quote has been sent to customer',
    color: 'outline',
    icon: 'FileText',
    isActive: true,
    order: 2,
    allowedTransitions: ['approved', 'rejected', 'expired'],
    autoExpireHours: 168, // 7 days
    isTerminal: false,
    category: 'quote',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'quote_sent',
    requiresAction: false,
    showsInQuotesList: true,
    showsInOrdersList: false,
    canBePaid: false,
    // Action permissions
    allowEdit: false,
    allowApproval: true,
    allowRejection: true,
    allowCartActions: false,
    allowCancellation: true,
    allowRenewal: false,
    allowShipping: false,
    allowAddressEdit: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: true,
    isSuccessful: false,
    countsAsOrder: false,
    progressPercentage: 40,
    // Customer messaging
    customerMessage: 'Your quote is ready for review',
    customerActionText: 'Review Quote',
    // CSS styling
    cssClass: 'status-sent',
    badgeVariant: 'outline',
  },
  {
    id: 'approved',
    name: 'approved',
    label: 'Approved',
    description: 'Customer has approved the quote',
    color: 'default',
    icon: 'CheckCircle',
    isActive: true,
    order: 3,
    allowedTransitions: ['payment_pending', 'paid'],
    isTerminal: false,
    category: 'quote',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'quote_approved',
    requiresAction: false,
    showsInQuotesList: true,
    showsInOrdersList: false,
    canBePaid: true,
    // Action permissions
    allowEdit: false,
    allowApproval: false,
    allowRejection: true,
    allowCartActions: true,
    allowCancellation: true,
    allowRenewal: false,
    allowShipping: false,
    allowAddressEdit: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: true,
    isSuccessful: true,
    countsAsOrder: false,
    progressPercentage: 60,
    // Customer messaging
    customerMessage: 'Approved - Ready to add to cart',
    customerActionText: 'Add to Cart',
    // CSS styling
    cssClass: 'status-approved',
    badgeVariant: 'default',
  },
  {
    id: 'rejected',
    name: 'rejected',
    label: 'Rejected',
    description: 'Quote has been rejected',
    color: 'destructive',
    icon: 'XCircle',
    isActive: true,
    order: 4,
    allowedTransitions: ['pending'],
    isTerminal: true,
    category: 'quote',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'quote_rejected',
    requiresAction: false,
    showsInQuotesList: true,
    showsInOrdersList: false,
    canBePaid: false,
    // Action permissions
    allowEdit: false,
    allowApproval: true,
    allowRejection: false,
    allowCartActions: false,
    allowCancellation: false,
    allowRenewal: false,
    allowShipping: false,
    allowAddressEdit: false,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: false,
    isSuccessful: false,
    countsAsOrder: false,
    progressPercentage: 0,
    // Customer messaging
    customerMessage: 'Quote has been rejected',
    customerActionText: 'Request New Quote',
    // CSS styling
    cssClass: 'status-rejected',
    badgeVariant: 'destructive',
  },
  {
    id: 'expired',
    name: 'expired',
    label: 'Expired',
    description: 'Quote has expired',
    color: 'destructive',
    icon: 'AlertTriangle',
    isActive: true,
    order: 5,
    allowedTransitions: ['pending'],
    isTerminal: true,
    category: 'quote',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'quote_expired',
    requiresAction: false,
    showsInQuotesList: true,
    showsInOrdersList: false,
    canBePaid: false,
  },
  {
    id: 'calculated',
    name: 'calculated',
    label: 'Calculated',
    description: 'Quote has been calculated and is ready for review',
    color: 'secondary',
    icon: 'Calculator',
    isActive: true,
    order: 6,
    allowedTransitions: ['sent', 'approved', 'rejected'],
    isTerminal: false,
    category: 'quote',
    // Flow properties
    triggersEmail: false,
    requiresAction: true,
    showsInQuotesList: true,
    showsInOrdersList: false,
    canBePaid: false,
  },
];

const defaultOrderStatuses: StatusConfig[] = [
  {
    id: 'payment_pending',
    name: 'payment_pending',
    label: 'Awaiting Payment',
    description: 'Order placed, awaiting payment verification',
    color: 'outline',
    icon: 'Clock',
    isActive: true,
    order: 1,
    allowedTransitions: ['paid', 'ordered', 'cancelled', 'partial_paid'],
    isTerminal: false,
    category: 'order',
    // Payment properties
    paymentType: 'prepaid',
    requiresPaymentBefore: 'processing',
    paymentValidationRule: 'full_payment',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'payment_instructions',
    requiresAction: false,
    showsInQuotesList: false,
    showsInOrdersList: true,
    canBePaid: false,
    // Action permissions
    allowEdit: false,
    allowApproval: false,
    allowRejection: false,
    allowCartActions: false,
    allowCancellation: true,
    allowRenewal: false,
    allowShipping: false,
    allowAddressEdit: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: false,
    isSuccessful: false,
    countsAsOrder: true,
    progressPercentage: 70,
    // Customer messaging
    customerMessage: 'Order placed - Please complete payment',
    customerActionText: 'Pay Now',
    // CSS styling
    cssClass: 'status-payment-pending',
    badgeVariant: 'outline',
  },
  {
    id: 'processing',
    name: 'processing',
    label: 'Processing',
    description: 'Order is being processed and prepared for fulfillment',
    color: 'secondary',
    icon: 'Clock',
    isActive: true,
    order: 2,
    allowedTransitions: ['ordered', 'shipped', 'cancelled'],
    isTerminal: false,
    category: 'order',
    // Payment properties
    paymentType: 'prepaid',
    requiresPaymentBefore: 'processing',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'order_processing',
    requiresAction: true,
    showsInQuotesList: false,
    showsInOrdersList: true,
    canBePaid: false,
    // Action permissions
    allowEdit: false,
    allowApproval: false,
    allowRejection: false,
    allowCartActions: false,
    allowCancellation: true,
    allowRenewal: false,
    allowShipping: false,
    allowAddressEdit: false,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: false,
    isSuccessful: false,
    countsAsOrder: true,
    progressPercentage: 75,
    // Customer messaging
    customerMessage: 'Your order is being processed',
    customerActionText: 'View Status',
    // CSS styling
    cssClass: 'status-processing',
    badgeVariant: 'secondary',
  },
  {
    id: 'paid',
    name: 'paid',
    label: 'Paid',
    description: 'Payment has been received',
    color: 'default',
    icon: 'DollarSign',
    isActive: true,
    order: 3,
    allowedTransitions: ['ordered', 'shipped', 'cancelled'],
    isTerminal: false,
    category: 'order',
    // Payment properties
    paymentType: 'prepaid',
    paymentValidationRule: 'full_payment',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'payment_received',
    requiresAction: true,
    showsInQuotesList: false,
    showsInOrdersList: true,
    canBePaid: false,
    // Action permissions
    allowEdit: false,
    allowApproval: false,
    allowRejection: false,
    allowCartActions: false,
    allowCancellation: true,
    allowRenewal: false,
    allowShipping: true,
    allowAddressEdit: false,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: false,
    isSuccessful: true,
    countsAsOrder: true,
    progressPercentage: 80,
    // Customer messaging
    customerMessage: 'Payment received - Order being processed',
    customerActionText: 'View Order',
    // CSS styling
    cssClass: 'status-paid',
    badgeVariant: 'default',
  },
  {
    id: 'ordered',
    name: 'ordered',
    label: 'Ordered',
    description: 'Order has been placed with merchant',
    color: 'default',
    icon: 'ShoppingCart',
    isActive: true,
    order: 4,
    allowedTransitions: ['shipped', 'cancelled'],
    isTerminal: false,
    category: 'order',
    // Payment properties - mixed support for various payment types
    paymentType: 'mixed',
    requiresPaymentBefore: 'shipping',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'order_placed',
    requiresAction: false,
    showsInQuotesList: false,
    showsInOrdersList: true,
    canBePaid: false,
    allowShipping: true,
    // Action permissions
    allowEdit: false,
    allowApproval: false,
    allowRejection: false,
    allowCartActions: false,
    allowCancellation: true,
    allowRenewal: false,
    allowAddressEdit: false,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: false,
    isSuccessful: true,
    countsAsOrder: true,
    progressPercentage: 85,
    // Customer messaging
    customerMessage: 'Order placed with merchant',
    customerActionText: 'View Order',
    // CSS styling
    cssClass: 'status-ordered',
    badgeVariant: 'default',
  },
  {
    id: 'shipped',
    name: 'shipped',
    label: 'Shipped',
    description: 'Order has been shipped',
    color: 'secondary',
    icon: 'Truck',
    isActive: true,
    order: 5,
    allowedTransitions: ['completed', 'cancelled', 'balance_due', 'cod_collected'],
    isTerminal: false,
    category: 'order',
    // Payment properties
    paymentType: 'mixed',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'order_shipped',
    requiresAction: false,
    showsInQuotesList: false,
    showsInOrdersList: true,
    canBePaid: false,
    // Action permissions
    allowEdit: false,
    allowApproval: false,
    allowRejection: false,
    allowCartActions: false,
    allowCancellation: true,
    allowRenewal: false,
    allowShipping: true,
    allowAddressEdit: false,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: false,
    isSuccessful: true,
    countsAsOrder: true,
    progressPercentage: 90,
    // Customer messaging
    customerMessage: 'Your order is on the way!',
    customerActionText: 'Track Package',
    // CSS styling
    cssClass: 'status-shipped',
    badgeVariant: 'secondary',
  },
  {
    id: 'completed',
    name: 'completed',
    label: 'Completed',
    description: 'Order has been delivered',
    color: 'outline',
    icon: 'CheckCircle',
    isActive: true,
    order: 6,
    allowedTransitions: ['completed'],
    isTerminal: true,
    category: 'order',
    // Payment properties
    paymentType: 'mixed',
    paymentValidationRule: 'full_payment',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'order_completed',
    requiresAction: false,
    showsInQuotesList: false,
    showsInOrdersList: true,
    canBePaid: false,
    // Action permissions
    allowEdit: false,
    allowApproval: false,
    allowRejection: false,
    allowCartActions: false,
    allowCancellation: false,
    allowRenewal: false,
    allowShipping: false,
    allowAddressEdit: false,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: false,
    isSuccessful: true,
    countsAsOrder: true,
    progressPercentage: 100,
    // Customer messaging
    customerMessage: 'Order completed successfully!',
    customerActionText: 'Order Again',
    // CSS styling
    cssClass: 'status-completed',
    badgeVariant: 'outline',
  },
  // COD-specific statuses
  {
    id: 'cod_approved',
    name: 'cod_approved',
    label: 'COD Approved',
    description: 'Order approved for Cash on Delivery',
    color: 'outline',
    icon: 'Banknote',
    isActive: true,
    order: 7,
    allowedTransitions: ['ordered', 'shipped', 'cancelled'],
    isTerminal: false,
    category: 'order',
    // Payment properties
    paymentType: 'cod',
    requiresPaymentBefore: 'never',
    isCODStatus: true,
    paymentValidationRule: 'cod_allowed',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'cod_order_confirmed',
    requiresAction: false,
    showsInQuotesList: false,
    showsInOrdersList: true,
    canBePaid: false,
    // Action permissions
    allowShipping: true, // Can ship without payment
    allowCancellation: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    countsAsOrder: true,
    progressPercentage: 70,
    // Customer messaging
    customerMessage: 'Order confirmed - Pay on delivery',
    customerActionText: 'Track Order',
    cssClass: 'status-cod-approved',
    badgeVariant: 'outline',
  },
  {
    id: 'partial_paid',
    name: 'partial_paid',
    label: 'Partially Paid',
    description: 'Partial payment received',
    color: 'secondary',
    icon: 'Clock',
    isActive: true,
    order: 8,
    allowedTransitions: ['ordered', 'balance_due', 'paid', 'cancelled'],
    isTerminal: false,
    category: 'order',
    // Payment properties
    paymentType: 'partial',
    minimumPaymentPercentage: 50, // Require 50% before shipping
    requiresPaymentBefore: 'shipping',
    paymentValidationRule: 'partial_payment',
    paymentMilestones: [
      { percentage: 50, label: 'Advance Payment', required: true },
      { percentage: 100, label: 'Full Payment', required: false }
    ],
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'partial_payment_received',
    requiresAction: false,
    showsInOrdersList: true,
    // Action permissions
    allowShipping: true, // If minimum payment met
    allowCancellation: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    countsAsOrder: true,
    progressPercentage: 75,
    // Customer messaging
    customerMessage: 'Advance payment received - Balance due on delivery',
    customerActionText: 'Pay Balance',
    cssClass: 'status-partial-paid',
    badgeVariant: 'secondary',
  },
  {
    id: 'balance_due',
    name: 'balance_due',
    label: 'Balance Due',
    description: 'Shipped with balance payment pending',
    color: 'outline',
    icon: 'AlertCircle',
    isActive: true,
    order: 9,
    allowedTransitions: ['paid', 'delivered', 'cod_collected'],
    isTerminal: false,
    category: 'order',
    // Payment properties
    paymentType: 'partial',
    requiresPaymentBefore: 'never', // Already shipped
    paymentValidationRule: 'partial_payment',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'balance_payment_reminder',
    requiresAction: true,
    showsInOrdersList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    countsAsOrder: true,
    progressPercentage: 85,
    // Customer messaging
    customerMessage: 'Order shipped - Balance payment due on delivery',
    customerActionText: 'Pay Now',
    cssClass: 'status-balance-due',
    badgeVariant: 'outline',
  },
  {
    id: 'cod_collected',
    name: 'cod_collected',
    label: 'COD Collected',
    description: 'Cash collected by delivery agent',
    color: 'default',
    icon: 'CheckCircle',
    isActive: true,
    order: 10,
    allowedTransitions: ['delivered', 'cod_remitted'],
    isTerminal: false,
    category: 'order',
    // Payment properties
    paymentType: 'cod',
    isCODStatus: true,
    codCollectionRequired: true,
    codRemittanceTracking: true,
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'cod_collection_confirmed',
    showsInOrdersList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    countsAsOrder: true,
    progressPercentage: 95,
    customerMessage: 'Payment collected successfully',
    cssClass: 'status-cod-collected',
    badgeVariant: 'default',
  },
  {
    id: 'cancelled',
    name: 'cancelled',
    label: 'Cancelled',
    description: 'Quote or order has been cancelled',
    color: 'destructive',
    icon: 'XCircle',
    isActive: true,
    order: 11,
    allowedTransitions: ['cancelled'],
    isTerminal: true,
    category: 'order',
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'order_cancelled',
    requiresAction: false,
    showsInQuotesList: true,
    showsInOrdersList: true,
    canBePaid: false,
    // Action permissions
    allowEdit: false,
    allowApproval: false,
    allowRejection: false,
    allowCartActions: false,
    allowCancellation: false,
    allowRenewal: false,
    allowShipping: false,
    allowAddressEdit: false,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    showExpiration: false,
    isSuccessful: false,
    countsAsOrder: false,
    progressPercentage: 0,
    // Customer messaging
    customerMessage: 'Order has been cancelled',
    customerActionText: 'Create New Order',
    // CSS styling
    cssClass: 'status-cancelled',
    badgeVariant: 'destructive',
  },
];

// Default package statuses for warehouse operations
const defaultPackageStatuses: StatusConfig[] = [
  {
    id: 'unclaimed',
    name: 'unclaimed',
    label: 'Unclaimed',
    description: 'Package received without customer identification',
    color: 'outline',
    icon: 'Package',
    isActive: true,
    order: 1,
    allowedTransitions: ['received', 'returned'],
    isTerminal: false,
    category: 'package',
    // Package-specific properties
    requiresInspection: true,
    allowsConsolidation: false,
    notifiesCustomer: false,
    trackingVisible: false,
    requiresPhotos: true,
    warehouseActionRequired: true,
    shippingEligible: false,
    // Flow properties
    triggersEmail: false,
    requiresAction: true,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: false,
    showInAdminView: true,
    isSuccessful: false,
    progressPercentage: 5,
    // Customer messaging
    customerMessage: 'Package is being processed',
    cssClass: 'status-unclaimed',
    badgeVariant: 'outline',
  },
  {
    id: 'received',
    name: 'received',
    label: 'Received',
    description: 'Package received and assigned to customer',
    color: 'secondary',
    icon: 'Package',
    isActive: true,
    order: 2,
    allowedTransitions: ['processing', 'inspection_required', 'damaged'],
    isTerminal: false,
    category: 'package',
    // Package-specific properties
    requiresInspection: false,
    allowsConsolidation: false,
    notifiesCustomer: true,
    trackingVisible: true,
    requiresPhotos: false,
    warehouseActionRequired: false,
    shippingEligible: false,
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'package_received',
    requiresAction: false,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: false,
    progressPercentage: 15,
    // Customer messaging
    customerMessage: 'Your package has been received at our warehouse',
    customerActionText: 'View Package',
    cssClass: 'status-received',
    badgeVariant: 'secondary',
  },
  {
    id: 'processing',
    name: 'processing',
    label: 'Processing',
    description: 'Package is being processed and inspected',
    color: 'outline',
    icon: 'Settings',
    isActive: true,
    order: 3,
    allowedTransitions: ['inspected', 'stored', 'damaged'],
    isTerminal: false,
    category: 'package',
    // Package-specific properties
    requiresInspection: true,
    allowsConsolidation: false,
    notifiesCustomer: false,
    trackingVisible: true,
    requiresPhotos: true,
    warehouseActionRequired: true,
    shippingEligible: false,
    // Flow properties
    triggersEmail: false,
    requiresAction: true,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: false,
    progressPercentage: 30,
    // Customer messaging
    customerMessage: 'Your package is being processed',
    cssClass: 'status-processing',
    badgeVariant: 'outline',
  },
  {
    id: 'inspected',
    name: 'inspected',
    label: 'Inspected',
    description: 'Package has been inspected and verified',
    color: 'default',
    icon: 'CheckCircle',
    isActive: true,
    order: 4,
    allowedTransitions: ['stored', 'consolidation_requested', 'ready_for_consolidation'],
    isTerminal: false,
    category: 'package',
    // Package-specific properties
    requiresInspection: false,
    allowsConsolidation: true,
    notifiesCustomer: true,
    trackingVisible: true,
    requiresPhotos: false,
    warehouseActionRequired: false,
    shippingEligible: false,
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'package_inspected',
    requiresAction: false,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: true,
    progressPercentage: 45,
    // Customer messaging
    customerMessage: 'Your package has been inspected and is ready for consolidation',
    customerActionText: 'Request Consolidation',
    cssClass: 'status-inspected',
    badgeVariant: 'default',
  },
  {
    id: 'stored',
    name: 'stored',
    label: 'Stored',
    description: 'Package is safely stored in warehouse',
    color: 'secondary',
    icon: 'Archive',
    isActive: true,
    order: 5,
    allowedTransitions: ['consolidation_requested', 'packed', 'shipped'],
    isTerminal: false,
    category: 'package',
    // Package-specific properties
    requiresInspection: false,
    allowsConsolidation: true,
    notifiesCustomer: false,
    trackingVisible: true,
    requiresPhotos: false,
    warehouseActionRequired: false,
    shippingEligible: true,
    // Flow properties
    triggersEmail: false,
    requiresAction: false,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: true,
    progressPercentage: 60,
    // Customer messaging
    customerMessage: 'Your package is safely stored and ready for shipping',
    customerActionText: 'Request Shipping',
    cssClass: 'status-stored',
    badgeVariant: 'secondary',
  },
  {
    id: 'consolidation_requested',
    name: 'consolidation_requested',
    label: 'Consolidation Requested',
    description: 'Customer has requested package consolidation',
    color: 'outline',
    icon: 'Package2',
    isActive: true,
    order: 6,
    allowedTransitions: ['consolidated', 'packed'],
    isTerminal: false,
    category: 'package',
    // Package-specific properties
    requiresInspection: false,
    allowsConsolidation: true,
    notifiesCustomer: false,
    trackingVisible: true,
    requiresPhotos: false,
    warehouseActionRequired: true,
    shippingEligible: false,
    // Flow properties
    triggersEmail: false,
    requiresAction: true,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: false,
    progressPercentage: 65,
    // Customer messaging
    customerMessage: 'Your consolidation request is being processed',
    cssClass: 'status-consolidation-requested',
    badgeVariant: 'outline',
  },
  {
    id: 'packed',
    name: 'packed',
    label: 'Packed',
    description: 'Package is packed and ready for shipment',
    color: 'default',
    icon: 'PackageCheck',
    isActive: true,
    order: 7,
    allowedTransitions: ['shipped'],
    isTerminal: false,
    category: 'package',
    // Package-specific properties
    requiresInspection: false,
    allowsConsolidation: false,
    notifiesCustomer: true,
    trackingVisible: true,
    requiresPhotos: true,
    warehouseActionRequired: false,
    shippingEligible: true,
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'package_packed',
    requiresAction: false,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: true,
    progressPercentage: 80,
    // Customer messaging
    customerMessage: 'Your package is packed and ready to ship',
    customerActionText: 'Track Shipping',
    cssClass: 'status-packed',
    badgeVariant: 'default',
  },
  {
    id: 'shipped',
    name: 'shipped',
    label: 'Shipped',
    description: 'Package has been shipped from warehouse',
    color: 'secondary',
    icon: 'Truck',
    isActive: true,
    order: 8,
    allowedTransitions: ['in_transit', 'at_customs', 'delivered'],
    isTerminal: false,
    category: 'package',
    // Package-specific properties
    requiresInspection: false,
    allowsConsolidation: false,
    notifiesCustomer: true,
    trackingVisible: true,
    requiresPhotos: false,
    warehouseActionRequired: false,
    shippingEligible: false,
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'package_shipped',
    requiresAction: false,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: true,
    progressPercentage: 85,
    // Customer messaging
    customerMessage: 'Your package has been shipped',
    customerActionText: 'Track Package',
    cssClass: 'status-shipped',
    badgeVariant: 'secondary',
  },
  {
    id: 'delivered',
    name: 'delivered',
    label: 'Delivered',
    description: 'Package has been successfully delivered',
    color: 'default',
    icon: 'CheckCircle',
    isActive: true,
    order: 9,
    allowedTransitions: ['delivered'],
    isTerminal: true,
    category: 'package',
    // Package-specific properties
    requiresInspection: false,
    allowsConsolidation: false,
    notifiesCustomer: true,
    trackingVisible: true,
    requiresPhotos: false,
    warehouseActionRequired: false,
    shippingEligible: false,
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'package_delivered',
    requiresAction: false,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: true,
    progressPercentage: 100,
    // Customer messaging
    customerMessage: 'Your package has been delivered successfully!',
    customerActionText: 'Rate Service',
    cssClass: 'status-delivered',
    badgeVariant: 'default',
  },
  {
    id: 'damaged',
    name: 'damaged',
    label: 'Damaged',
    description: 'Package has been damaged',
    color: 'destructive',
    icon: 'AlertTriangle',
    isActive: true,
    order: 10,
    allowedTransitions: ['returned', 'repaired'],
    isTerminal: false,
    category: 'package',
    // Package-specific properties
    requiresInspection: true,
    allowsConsolidation: false,
    notifiesCustomer: true,
    trackingVisible: true,
    requiresPhotos: true,
    warehouseActionRequired: true,
    shippingEligible: false,
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'package_damaged',
    requiresAction: true,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: false,
    progressPercentage: 25,
    // Customer messaging
    customerMessage: 'Your package has been damaged - we are reviewing options',
    customerActionText: 'Contact Support',
    cssClass: 'status-damaged',
    badgeVariant: 'destructive',
  },
  {
    id: 'returned',
    name: 'returned',
    label: 'Returned',
    description: 'Package has been returned to sender',
    color: 'destructive',
    icon: 'RotateCcw',
    isActive: true,
    order: 11,
    allowedTransitions: ['returned'],
    isTerminal: true,
    category: 'package',
    // Package-specific properties
    requiresInspection: false,
    allowsConsolidation: false,
    notifiesCustomer: true,
    trackingVisible: true,
    requiresPhotos: false,
    warehouseActionRequired: false,
    shippingEligible: false,
    // Flow properties
    triggersEmail: true,
    emailTemplate: 'package_returned',
    requiresAction: false,
    showsInPackagesList: true,
    // Display properties
    showInCustomerView: true,
    showInAdminView: true,
    isSuccessful: false,
    progressPercentage: 0,
    // Customer messaging
    customerMessage: 'Package has been returned to sender',
    customerActionText: 'Contact Support',
    cssClass: 'status-returned',
    badgeVariant: 'destructive',
  },
];

export const StatusConfigProvider = ({ children }: { children: ReactNode }) => {
  // CRITICAL DEBUG: Track StatusConfigProvider re-renders
  const statusProviderRenderTimestamp = Date.now();

  // EMERGENCY CIRCUIT BREAKER: Prevent infinite re-renders
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  
  if (renderCountRef.current > 100) {
    console.error('[admin-user-chat] 🚨 EMERGENCY: StatusConfigProvider render count exceeded 100! Applying circuit breaker.');
    // Return a static fallback provider to break the infinite loop
    return (
      <StatusConfigContext.Provider value={{
        quoteStatuses: defaultQuoteStatuses,
        orderStatuses: defaultOrderStatuses,
        packageStatuses: defaultPackageStatuses,
        isLoading: false,
        error: 'Circuit breaker activated - too many re-renders',
        refreshData: async () => {},
        lastUpdated: Date.now()
      }}>
        {children}
      </StatusConfigContext.Provider>
    );
  }

  const [quoteStatuses, setQuoteStatuses] = useState<StatusConfig[]>(defaultQuoteStatuses);
  const [orderStatuses, setOrderStatuses] = useState<StatusConfig[]>(defaultOrderStatuses);
  const [packageStatuses, setPackageStatuses] = useState<StatusConfig[]>(defaultPackageStatuses);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now());

  // Use refs to prevent unnecessary re-renders in subscription callbacks
  const quoteStatusesRef = useRef(quoteStatuses);
  const orderStatusesRef = useRef(orderStatuses);
  const packageStatusesRef = useRef(packageStatuses);
  
  // Update refs when state changes
  useEffect(() => {
    quoteStatusesRef.current = quoteStatuses;
  }, [quoteStatuses]);
  
  useEffect(() => {
    orderStatusesRef.current = orderStatuses;
  }, [orderStatuses]);
  
  useEffect(() => {
    packageStatusesRef.current = packageStatuses;
  }, [packageStatuses]);

  const loadStatusSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      
      const result = await lambdaDataService.getSystemSettings(['quote_statuses', 'order_statuses', 'package_statuses']);
      if (!result.success) throw new Error('Failed to fetch system settings');
      const data = result.data;

      const quoteSettings = data?.find((s) => s.setting_key === 'quote_statuses');
      const orderSettings = data?.find((s) => s.setting_key === 'order_statuses');
      const packageSettings = data?.find((s) => s.setting_key === 'package_statuses');

      // If database is empty, initialize with defaults (admin only)
      if (!quoteSettings || !orderSettings || !packageSettings) {
        console.log('📝 Database empty - checking if user can initialize defaults...');
        
        // Check if current user is admin before attempting initialization
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log('⚠️ No authenticated user - using fallback defaults');
          setQuoteStatuses(defaultQuoteStatuses);
          setOrderStatuses(defaultOrderStatuses);
          setPackageStatuses(defaultPackageStatuses);
          return;
        }

        // Check admin status using Real Profile Service (following updated pattern)
        let hasAdminAccess = false;

        try {
          // Use real profile service for roles instead of Supabase
          const { realProfileService } = await import('@/services/RealProfileService');
          const roles = await realProfileService.getUserRoles(user.id);

          hasAdminAccess = roles.includes('admin') || roles.includes('moderator');
          console.log('🔐 Role check completed:', {
            userId: user.id,
            hasAdminAccess,
            userRoles: roles
          });
        } catch (error) {
          console.warn('Exception in admin check, falling back to non-admin:', error);
          hasAdminAccess = false;
        }
        
        if (!hasAdminAccess) {
          console.log('⚠️ Non-admin user - using fallback defaults');
          setQuoteStatuses(defaultQuoteStatuses);
          setOrderStatuses(defaultOrderStatuses);
          setPackageStatuses(defaultPackageStatuses);
          return;
        }

        console.log('🏗️ Admin user - initializing default statuses in database...');
        await initializeDefaultStatuses();
        return; // Will reload after initialization
      }

      // Load from database
      if (quoteSettings?.setting_value) {
        try {
          // Clean up invalid escapes before parsing
          const cleanedJSON = quoteSettings.setting_value.replace(/\\!/g, '!');
          const loadedQuoteStatuses = JSON.parse(cleanedJSON);
          setQuoteStatuses(loadedQuoteStatuses);
        } catch (e) {
          const errorMessage = e instanceof Error ? e.message : 'Unknown error';
          console.error('Error parsing quote statuses JSON:', errorMessage);
          console.error('Invalid JSON content:', quoteSettings.setting_value);
          throw new Error(`Invalid JSON in quote_statuses: ${errorMessage}`);
        }
      }
      if (orderSettings?.setting_value) {
        try {
          // Clean up invalid escapes before parsing
          const cleanedJSON = orderSettings.setting_value.replace(/\\!/g, '!');
          const loadedOrderStatuses = JSON.parse(cleanedJSON);
          setOrderStatuses(loadedOrderStatuses);
        } catch (e) {
          const errorMessage = e instanceof Error ? e.message : 'Unknown error';
          console.error('Error parsing order statuses JSON:', errorMessage);
          console.error('Invalid JSON content:', orderSettings.setting_value);
          throw new Error(`Invalid JSON in order_statuses: ${errorMessage}`);
        }
      }
      if (packageSettings?.setting_value) {
        try {
          // Clean up invalid escapes before parsing
          const cleanedJSON = packageSettings.setting_value.replace(/\\!/g, '!');
          const loadedPackageStatuses = JSON.parse(cleanedJSON);
          setPackageStatuses(loadedPackageStatuses);
        } catch (e) {
          const errorMessage = e instanceof Error ? e.message : 'Unknown error';
          console.error('Error parsing package statuses JSON:', errorMessage);
          console.error('Invalid JSON content:', packageSettings.setting_value);
          throw new Error(`Invalid JSON in package_statuses: ${errorMessage}`);
        }
      }

      // Update timestamp to trigger re-renders
      setLastUpdated(Date.now());
    } catch (err) {
      console.error('❌ Failed to load status config:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load status config';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []); // useCallback dependency array

  const initializeDefaultStatuses = async () => {
    try {
      console.log('🏗️ Initializing default statuses using Lambda Data Service...');

      // Use Lambda Data Service to save default settings instead of Supabase
      const statusSettings = [
        {
          setting_key: 'quote_statuses',
          setting_value: JSON.stringify(defaultQuoteStatuses)
        },
        {
          setting_key: 'order_statuses',
          setting_value: JSON.stringify(defaultOrderStatuses)
        },
        {
          setting_key: 'package_statuses',
          setting_value: JSON.stringify(defaultPackageStatuses)
        }
      ];

      // TODO: Implement updateSystemSettings method in Lambda Data Service
      // For now, use local defaults until Lambda endpoint is ready
      console.log('📝 Lambda Data Service updateSystemSettings not yet implemented - using local defaults');

      console.log('✅ Default statuses initialized successfully (locally)');

      // Set local state to defaults
      setQuoteStatuses(defaultQuoteStatuses);
      setOrderStatuses(defaultOrderStatuses);
      setPackageStatuses(defaultPackageStatuses);
      setLastUpdated(Date.now());
    } catch (error) {
      console.error('❌ Failed to initialize default statuses:', error);
      // Fallback to defaults in memory if database initialization fails
      setQuoteStatuses(defaultQuoteStatuses);
      setOrderStatuses(defaultOrderStatuses);
      setPackageStatuses(defaultPackageStatuses);
      setLastUpdated(Date.now());
    }
  };

  const refreshData = useCallback(async () => {
    await loadStatusSettings();
  }, [loadStatusSettings]);

  // CRITICAL: Memoize the context value to prevent constant re-renders
  const contextValue = useMemo(() => ({
    quoteStatuses,
    orderStatuses,
    packageStatuses,
    isLoading,
    error,
    refreshData,
    lastUpdated
  }), [quoteStatuses, orderStatuses, packageStatuses, isLoading, error, refreshData, lastUpdated]);

  useEffect(() => {
    loadStatusSettings();

    // DISABLED: Real-time subscription for status changes to prevent WebSocket errors
    // Realtime is not enabled on Supabase project
    // const subscription = supabase
    //   .channel('status-config-changes')
    //   .on(
    //     'postgres_changes',
    //     {
    //       event: '*', // Listen to all changes (INSERT, UPDATE, DELETE)
    //       schema: 'public',
    //       table: 'system_settings',
    //       filter: 'setting_key=in.(quote_statuses,order_statuses)',
    //     },
    //     (payload) => {
    //
    //       // Simple debounce without depending on lastUpdated to avoid infinite loops
    //       loadStatusSettings();
    //     },
    //   )
    //   .subscribe((status) => {
    //
    //   });

    // return () => {
    //
    //   subscription.unsubscribe();
    // };
  }, [loadStatusSettings]); // Remove lastUpdated to prevent infinite loop

  return (
    <StatusConfigContext.Provider value={contextValue}>
      {children}
    </StatusConfigContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useStatusConfig() {
  const ctx = useContext(StatusConfigContext);
  if (!ctx) throw new Error('useStatusConfig must be used within a StatusConfigProvider');
  return ctx;
}
